import { useCallback, useEffect, useRef, useState } from 'react'

/** How long `copied` reads true after a copy lands. */
const COPIED_MS = 1200

/**
 * Click to copy, with `copied` true for a moment once it has landed — the
 * confirmation is the control's own label changing, never a toast, because a
 * copy is not an event worth one of the three toast slots.
 *
 * A denied clipboard permission is swallowed: it is not worth an error
 * surface, and the value the user wanted is still on screen to select.
 */
export function useCopy(): { copied: boolean; copy: (text: string) => void } {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current !== null) globalThis.clearTimeout(timer.current)
    },
    [],
  )
  const copy = useCallback((text: string) => {
    void globalThis.navigator?.clipboard?.writeText(text).then(
      () => {
        setCopied(true)
        if (timer.current !== null) globalThis.clearTimeout(timer.current)
        timer.current = globalThis.setTimeout(() => setCopied(false), COPIED_MS)
      },
      () => {},
    )
  }, [])
  return { copied, copy }
}
